import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { alpha } from '@mui/material/styles';
import {
  Box,
  Tab,
  List,
  Tabs,
  Stack,
  Avatar,
  Button,
  ListItem,
  Checkbox,
  useTheme,
  TextField,
  Typography,
  ListItemText,
  InputAdornment,
  ListItemAvatar,
  FormControlLabel,
} from '@mui/material';

import { useAuth } from 'src/hooks/useAuth';

import { fetchAllGroups } from 'src/services/group.users.service';
import { fetchUsers, getUserAvatarUrl } from 'src/services/user.service';
import { addMultipleUsersPermissions } from 'src/services/ticket.permissions.service';

import Iconify from 'src/components/iconify';

import { listTicketModalStyle } from '../styles';

AddUserPermissionModal.propTypes = {
  ticketId: PropTypes.any,
  existingUsers: PropTypes.array,
  onRefresh: PropTypes.func,
  hideModal: PropTypes.func,
};

export default function AddUserPermissionModal({
  ticketId,
  existingUsers = [],
  onRefresh,
  hideModal,
}) {
  const { t } = useTranslation();
  const { id: currentUserId } = useAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [isRead, setIsRead] = useState(true);
  const [isWrite, setIsWrite] = useState(false);

  useEffect(() => {
    fetchUsers().then((res) => {
      const existingIds = new Set(existingUsers.map((u) => u.id));

      const mappedUsers = (res || []).map((u) => ({
        ...u,
        avatarUrl: u.avatarVersion ? getUserAvatarUrl(u.id, u.avatarVersion) : null,
      }));

      setUsers(mappedUsers.filter((u) => !existingIds.has(u.id)));
    });
  }, [existingUsers]);

  useEffect(() => {
    if (currentUserId) {
      fetchAllGroups(currentUserId)
        .then((res) => {
          const userCreatedGroups = (res || []).filter(
            (g) => g.createdBy === currentUserId
          );
          setGroups(userCreatedGroups);
        })
        .catch((err) => console.error('Failed to load user groups:', err));
    }
  }, [currentUserId]);

  const toggleUser = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const toggleGroup = (group) => {
    const existingIds = new Set(existingUsers.map((u) => u.id));
    const eligibleGroupUserIds = (group.users || [])
      .map((u) => u.id)
      .filter((id) => !existingIds.has(id));

    if (eligibleGroupUserIds.length === 0) return;

    const allSelected = eligibleGroupUserIds.every((id) => selectedIds.includes(id));

    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !eligibleGroupUserIds.includes(id)));
    } else {
      setSelectedIds((prev) => {
        const unique = new Set([...prev, ...eligibleGroupUserIds]);
        return Array.from(unique);
      });
    }
  };

  const handleCommit = async () => {
    if (selectedIds.length === 0) return;
    try {
      await addMultipleUsersPermissions({
        ticketId,
        userIds: selectedIds,
        isReadPermission: isRead,
        isWritePermission: isWrite,
      });

      if (onRefresh) onRefresh();
      if (hideModal) hideModal();
    } catch (e) {
      console.error('Failed to add users:', e);
    }
  };

  const filteredUsers = users.filter((u) =>
    `${u.givenName} ${u.familyName}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Stack spacing={2.5} p={2.5} sx={{ maxHeight: '79vh', overflow: 'hidden' }}>
      {/* Search Input placed above the tabs */}
      <TextField
        fullWidth
        size="small"
        placeholder={t('filters.search')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
            </InputAdornment>
          ),
        }}
      />

      {/* Tabs left-aligned */}
      <Tabs
        value={activeTab}
        onChange={(e, val) => setActiveTab(val)}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          label={`${t('pages.ticketDetails.users') || 'User Permission List'} (${
            filteredUsers.length
          })`}
          value="users"
          sx={{ fontWeight: 700 }}
        />
        <Tab
          label={`${t('ticket.groups') || 'Groups'} (${groups.length})`}
          value="groups"
          sx={{ fontWeight: 700 }}
        />
      </Tabs>

      {/* List container without inner card framing wrapper */}
      <Box sx={{ px: 0.5 }}>
        {activeTab === 'users' && (
          <List
            sx={{
              ...listTicketModalStyle,
              maxHeight: '48vh',
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': { width: '6px', height: '6px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)',
                borderRadius: '10px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.18)',
              },
            }}
          >
            {filteredUsers.map((u) => (
              <ListItem
                key={u.id}
                divider
                button
                onClick={() => toggleUser(u.id)}
                secondaryAction={
                  <Checkbox
                    edge="end"
                    checked={selectedIds.includes(u.id)}
                    disableRipple
                    sx={{
                      color: theme.palette.success.main,
                      '&.Mui-checked': { color: theme.palette.success.main },
                    }}
                  />
                }
              >
                <ListItemAvatar>
                  <Avatar
                    src={u.avatarUrl}
                    sx={{
                      width: 40,
                      height: 40,
                      border: isDark
                        ? '1px solid rgba(255,255,255,0.05)'
                        : '1px solid rgba(0,0,0,0.05)',
                    }}
                    imgProps={{ loading: 'lazy' }}
                  >
                    {u.givenName ? u.givenName.charAt(0).toUpperCase() : ''}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={`${u.givenName} ${u.familyName}`}
                  secondary={u.email}
                  primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 800 }}
                  secondaryTypographyProps={{ variant: 'caption', fontWeight: 600 }}
                />
              </ListItem>
            ))}
          </List>
        )}

        {activeTab === 'groups' && (
          <List
            sx={{
              ...listTicketModalStyle,
              maxHeight: '48vh',
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': { width: '6px', height: '6px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)',
                borderRadius: '10px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.18)',
              },
            }}
          >
            {groups.length > 0 ? (
              groups.map((group) => {
                const existingIds = new Set(existingUsers.map((u) => u.id));
                const eligibleUsers = (group.users || []).filter(
                  (u) => !existingIds.has(u.id)
                );
                const isGroupFullySelected =
                  eligibleUsers.length > 0 &&
                  eligibleUsers.every((u) => selectedIds.includes(u.id));
                const skippedCount = (group.users || []).length - eligibleUsers.length;

                return (
                  <ListItem
                    key={group.id}
                    divider
                    button
                    onClick={() => toggleGroup(group)}
                    secondaryAction={
                      <Checkbox
                        edge="end"
                        checked={isGroupFullySelected}
                        disabled={eligibleUsers.length === 0}
                        disableRipple
                        sx={{
                          color: theme.palette.success.main,
                          '&.Mui-checked': { color: theme.palette.success.main },
                        }}
                      />
                    }
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={group.image}
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: alpha(theme.palette.success.main, 0.1),
                          color: theme.palette.success.main,
                          border: isDark
                            ? '1px solid rgba(255,255,255,0.05)'
                            : '1px solid rgba(0,0,0,0.05)',
                          fontWeight: 800,
                        }}
                      >
                        {group.name.charAt(0).toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={group.name}
                      secondary={
                        <>
                          {`${eligibleUsers.length} eligible members`}
                          {skippedCount > 0 &&
                            ` (${skippedCount} already in ticket skipped)`}
                        </>
                      }
                      primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 800 }}
                      secondaryTypographyProps={{ variant: 'caption', fontWeight: 600 }}
                    />
                  </ListItem>
                );
              })
            ) : (
              <Typography
                textAlign="center"
                variant="subtitle2"
                sx={{ p: 4, opacity: 0.6 }}
              >
                No groups found.
              </Typography>
            )}
          </List>
        )}
      </Box>

      {/* Permissions Select Box */}
      <Stack
        direction="row"
        spacing={4}
        justifyContent="center"
        sx={{
          py: 1.5,
          bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#F4F6F8',
          borderRadius: '12px',
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={isRead}
              onChange={(e) => setIsRead(e.target.checked)}
              disabled
              sx={{
                color: theme.palette.success.main,
                '&.Mui-checked': { color: theme.palette.success.main, opacity: 0.5 },
              }}
            />
          }
          label={
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Read
            </Typography>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={isWrite}
              onChange={(e) => setIsWrite(e.target.checked)}
              sx={{
                color: theme.palette.success.main,
                '&.Mui-checked': { color: theme.palette.success.main },
              }}
            />
          }
          label={
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Write
            </Typography>
          }
        />
      </Stack>

      <Button
        fullWidth
        variant="contained"
        sx={{
          bgcolor: theme.palette.success.main,
          borderRadius: '12px',
          py: 1.2,
          fontWeight: 800,
          color: '#FFF',
          '&:hover': {
            bgcolor: theme.palette.success.dark || theme.palette.success.main,
          },
          '&.Mui-disabled': {
            bgcolor: theme.palette.action.disabledBackground,
            color: theme.palette.text.disabled,
          },
        }}
        disabled={selectedIds.length === 0}
        onClick={handleCommit}
      >
        {t('ticket.addUsersToTicket')} ({selectedIds.length})
      </Button>
    </Stack>
  );
}
